import React from 'react';
import GuestHeader from '../../layout/GuestHeader'
import Footer from '../../layout/Footer';
import {Helmet} from "react-helmet";
import CookiePreferences from '../../components/CookiePreferences';

const CookiePolicy = () => {

  return (
    <>
      <Helmet>
          <title>Cookie Policy | Oreca Pro</title>
          <link rel="canonical" href={window.location.href} />
      </Helmet>
      <GuestHeader />
      <section className='guest-section relative min-h-[100vh] pb-20'>
        <div className='w-full max-w-[1000px] relative mx-auto text-left px-4 pt-[120px] md:pt-[160px]'>
          <h1 className='text-3xl font-semibold my-5 text-center'>Cookie Policy</h1>
          <p className='text-sm text-gray-400 mb-7 text-center'>Last Updated: November 2025</p>
          
          <div className='space-y-6 text-gray-300'>
            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>1. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your device (computer, smartphone, tablet) when you visit a website or use an application. 
                They help the website remember your actions and preferences over time, so you don't have to re-enter them whenever you return.
              </p>
              <p className='mt-2'>
                In addition to traditional cookies, Oreca Pro uses browser local storage, which serves a similar purpose but stores data 
                directly in your browser rather than being sent with every server request.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>2. How We Use Cookies</h2>
              <p>Oreca Pro uses cookies and local storage for two primary purposes:</p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>3. Essential Cookies (Strictly Necessary)</h2>
              <p className='font-semibold mb-2'>These cookies are essential for the App to function and cannot be disabled.</p>
              
              <div className='bg-custom-charcoal/50 p-4 rounded-lg mt-3'>
                <h3 className='text-lg font-semibold text-white mb-2'>Authentication Cookie</h3>
                <ul className='list-none space-y-2 ml-2'>
                  <li><strong>Name:</strong> <code className='bg-custom-charcoal px-2 py-1 rounded'>access_token</code></li>
                  <li><strong>Purpose:</strong> Stores your login session token to keep you logged in</li>
                  <li><strong>Type:</strong> HTTP Cookie</li>
                  <li><strong>Duration:</strong> 7 days</li>
                  <li><strong>Why Essential:</strong> Without this cookie, you cannot access your account or use the App. The App requires 
                  authentication to save spots, view forecasts, and access personalized features.</li>
                </ul>
              </div>

              <div className='bg-custom-charcoal/50 p-4 rounded-lg mt-3'>
                <h3 className='text-lg font-semibold text-white mb-2'>Browser Local Storage</h3>
                <ul className='list-none space-y-2 ml-2'>
                  <li><strong>Storage Key:</strong> <code className='bg-custom-charcoal px-2 py-1 rounded'>persist:root</code></li>
                  <li><strong>Purpose:</strong> Stores your app state including:
                    <ul className='list-disc list-inside ml-4 mt-1'>
                      <li>Authentication status</li>
                      <li>Saved spots and locations</li>
                      <li>Forecast data (cached for performance)</li>
                      <li>User preferences and settings</li>
                      <li>Cookie consent preferences</li>
                    </ul>
                  </li>
                  <li><strong>Type:</strong> Browser Local Storage (Redux Persist)</li>
                  <li><strong>Duration:</strong> Persistent until you clear browser data or delete your account</li>
                  <li><strong>Why Essential:</strong> This allows the App to remember your saved spots, preferences, and login state 
                  without requiring constant server requests. Without it, you would lose all your data every time you refresh the page.</li>
                </ul>
              </div>

              <p className='mt-4 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg'>
                <strong>GDPR Compliance Note:</strong> These cookies are classified as "strictly necessary" under GDPR Article 6(1)(b) 
                (necessary for contract performance) and do not require explicit consent as they are essential for providing the service you requested.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>4. Analytics Cookies (Optional)</h2>
              <p className='font-semibold mb-2'>These cookies require your consent and can be disabled.</p>
              
              <div className='bg-custom-charcoal/50 p-4 rounded-lg mt-3'>
                <h3 className='text-lg font-semibold text-white mb-2'>Google Analytics</h3>
                <ul className='list-none space-y-2 ml-2'>
                  <li><strong>Provider:</strong> Google LLC</li>
                  <li><strong>Purpose:</strong> Tracks how users interact with the App to help us improve the user experience</li>
                  <li><strong>Cookies Set:</strong> <code className='bg-custom-charcoal px-2 py-1 rounded'>_ga</code>, <code className='bg-custom-charcoal px-2 py-1 rounded'>_gid</code>, <code className='bg-custom-charcoal px-2 py-1 rounded'>_gat</code></li>
                  <li><strong>Duration:</strong> Up to 2 years</li>
                  <li><strong>Data Collected:</strong> 
                    <ul className='list-disc list-inside ml-4 mt-1'>
                      <li>Pages viewed and time spent</li>
                      <li>Device and browser information</li>
                      <li>Anonymized IP address</li>
                      <li>User interaction patterns</li>
                    </ul>
                  </li>
                  <li className='mt-2'><strong>Your Control:</strong> You can opt out of Google Analytics by:
                    <ul className='list-disc list-inside ml-4 mt-1'>
                      <li>Declining analytics cookies when first prompted</li>
                      <li>Changing your preferences in your browser settings</li>
                      <li>Installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className='text-custom-light-blue underline'>Google Analytics Opt-out Browser Add-on</a></li>
                    </ul>
                  </li>
                </ul>
              </div>

              <div className='bg-custom-charcoal/50 p-4 rounded-lg mt-3'>
                <h3 className='text-lg font-semibold text-white mb-2'>Google Tag Manager</h3>
                <ul className='list-none space-y-2 ml-2'>
                  <li><strong>Provider:</strong> Google LLC</li>
                  <li><strong>Purpose:</strong> Manages and deploys marketing and analytics tags</li>
                  <li><strong>Duration:</strong> Session-based</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>5. Third-Party Cookies</h2>
              <p>The following third-party services may set cookies when you use certain features:</p>
              
              <ul className='list-disc list-inside ml-4 mt-2 space-y-2'>
                <li><strong>Mapbox:</strong> Used for interactive map functionality. May set cookies for map tile caching and session management.</li>
                <li><strong>Open Meteo API:</strong> Does not set cookies; only receives location coordinates to fetch weather data.</li>
              </ul>
              
              <p className='mt-3'>
                We do not control these third-party cookies. Please refer to their respective privacy policies for more information.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>6. Managing Your Cookie Preferences</h2>
              
              <div className='mb-6'>
                <CookiePreferences />
              </div>

              <h3 className='text-lg font-semibold text-white mt-6 mb-2'>6.1 In-App Cookie Consent</h3>
              <p>
                Use the interactive cookie preference manager above to control your cookie settings in real-time. 
                When you first use Oreca Pro, you will be asked to accept or decline optional cookies (analytics). 
                You can change your preferences at any time by returning to this page.
              </p>

              <h3 className='text-lg font-semibold text-white mt-4 mb-2'>6.2 Browser Settings</h3>
              <p>
                You can control cookies through your browser settings. However, please note that disabling essential cookies 
                (authentication and local storage) will prevent you from using Oreca Pro.
              </p>
              <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
              </ul>

              <h3 className='text-lg font-semibold text-white mt-4 mb-2'>6.3 Clearing Cookies and Local Storage</h3>
              <p>
                You can clear all cookies and local storage at any time through your browser settings. This will log you out 
                and remove all saved data from your device (your account data on our servers will remain intact).
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>7. Do Not Track (DNT)</h2>
              <p>
                We respect Do Not Track (DNT) signals. If your browser has DNT enabled, we will not load Google Analytics 
                or other tracking scripts, even if you have previously consented to analytics cookies.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>8. Cookie Lifespan Summary</h2>
              
              <div className='overflow-x-auto mt-3'>
                <table className='w-full border-collapse'>
                  <thead>
                    <tr className='bg-custom-charcoal'>
                      <th className='border border-gray-600 p-3 text-left'>Cookie/Storage</th>
                      <th className='border border-gray-600 p-3 text-left'>Type</th>
                      <th className='border border-gray-600 p-3 text-left'>Duration</th>
                      <th className='border border-gray-600 p-3 text-left'>Consent Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='border border-gray-600 p-3'>access_token</td>
                      <td className='border border-gray-600 p-3'>Essential</td>
                      <td className='border border-gray-600 p-3'>7 days</td>
                      <td className='border border-gray-600 p-3'>No</td>
                    </tr>
                    <tr>
                      <td className='border border-gray-600 p-3'>persist:root</td>
                      <td className='border border-gray-600 p-3'>Essential</td>
                      <td className='border border-gray-600 p-3'>Persistent</td>
                      <td className='border border-gray-600 p-3'>No</td>
                    </tr>
                    <tr>
                      <td className='border border-gray-600 p-3'>Google Analytics (_ga, _gid)</td>
                      <td className='border border-gray-600 p-3'>Analytics</td>
                      <td className='border border-gray-600 p-3'>Up to 2 years</td>
                      <td className='border border-gray-600 p-3'>Yes</td>
                    </tr>
                    <tr>
                      <td className='border border-gray-600 p-3'>Google Tag Manager</td>
                      <td className='border border-gray-600 p-3'>Analytics</td>
                      <td className='border border-gray-600 p-3'>Session</td>
                      <td className='border border-gray-600 p-3'>Yes</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>9. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices or for legal reasons. 
                We will notify you of significant changes and may ask for renewed consent where required.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>10. Contact Us</h2>
              <p>
                If you have questions about our use of cookies, please contact us at: info@oreca.pro
              </p>
            </section>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CookiePolicy;
